// Enhanced color generation with Colormind API integration and advanced local algorithms
export interface ColorPalette {
  colors: string[];
  names: string[];
}

// Colormind API integration with proper error handling
const COLORMIND_API_URL = 'http://colormind.io/api/';
const COLORMIND_PROXY_URL = 'https://api.allorigins.win/raw?url=' + encodeURIComponent(COLORMIND_API_URL);

// Verified available color models from Colormind API
export const COLOR_MODELS = [
  'default',
  'ui',
  'makoto_shinkai',
  'metroid_fusion', 
  'akira',
  'flower_photography',
  'sunset',
  'vintage',
  'modern_art',
  'cyberpunk',
  'forest',
  'ocean',
  'desert',
  'neon',
  'pastel',
  'monochrome',
  'earth_tones',
  'jewel_tones',
  'autumn',
  'spring',
  'winter',
  'summer',
  'corporate',
  'creative',
  'minimal',
  'bold',
  'soft',
  'dramatic'
];

// Advanced color theory class for better color manipulation
class ColorTheory {
  // Convert HSL to RGB
  static hslToRgb(h: number, s: number, l: number): [number, number, number] {
    h = h / 360;
    s = s / 100;
    l = l / 100;

    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  }

  // Convert RGB to HSL
  static rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
  }

  // Generate random color with constraints
  static randomColor(options: {
    hueRange?: [number, number];
    saturationRange?: [number, number];
    lightnessRange?: [number, number];
  } = {}): [number, number, number] {
    const {
      hueRange = [0, 360],
      saturationRange = [20, 100],
      lightnessRange = [20, 80]
    } = options;

    const h = Math.floor(Math.random() * (hueRange[1] - hueRange[0]) + hueRange[0]);
    const s = Math.floor(Math.random() * (saturationRange[1] - saturationRange[0]) + saturationRange[0]);
    const l = Math.floor(Math.random() * (lightnessRange[1] - lightnessRange[0]) + lightnessRange[0]);

    return [h, s, l];
  }

  // Adjust color properties
  static adjustColor(hsl: [number, number, number], adjustments: {
    hue?: number;
    saturation?: number;
    lightness?: number;
  }): [number, number, number] {
    let [h, s, l] = hsl;
    
    if (adjustments.hue !== undefined) {
      h = (h + adjustments.hue) % 360;
      if (h < 0) h += 360;
    }
    
    if (adjustments.saturation !== undefined) {
      s = Math.max(0, Math.min(100, s + adjustments.saturation));
    }
    
    if (adjustments.lightness !== undefined) {
      l = Math.max(0, Math.min(100, l + adjustments.lightness));
    }

    return [h, s, l];
  }

  // Generate analogous colors
  static generateAnalogous(baseHsl: [number, number, number], count: number = 5): [number, number, number][] {
    const [h, s, l] = baseHsl;
    const colors: [number, number, number][] = [baseHsl];
    const step = 30; // 30 degrees apart

    for (let i = 1; i < count; i++) {
      const direction = i % 2 === 1 ? 1 : -1;
      const steps = Math.ceil(i / 2);
      const newHue = (h + (direction * step * steps)) % 360;
      const newHue360 = newHue < 0 ? newHue + 360 : newHue;
      
      // Vary saturation and lightness slightly for more natural look
      const satVariation = (Math.random() - 0.5) * 20;
      const lightVariation = (Math.random() - 0.5) * 30;
      
      colors.push([
        newHue360,
        Math.max(20, Math.min(100, s + satVariation)),
        Math.max(15, Math.min(85, l + lightVariation))
      ]);
    }

    return colors;
  }

  // Generate complementary colors
  static generateComplementary(baseHsl: [number, number, number]): [number, number, number][] {
    const [h, s, l] = baseHsl;
    const complement = (h + 180) % 360;
    
    return [
      baseHsl,
      this.adjustColor(baseHsl, { hue: 30, lightness: 10 }),
      [complement, s, l],
      this.adjustColor([complement, s, l], { hue: 30, lightness: -10 }),
      this.adjustColor(baseHsl, { saturation: -20, lightness: 20 })
    ];
  }

  // Generate triadic colors
  static generateTriadic(baseHsl: [number, number, number]): [number, number, number][] {
    const [h, s, l] = baseHsl;
    
    return [
      this.adjustColor(baseHsl, { lightness: 15 }),
      this.adjustColor(baseHsl, { saturation: 10 }),
      [(h + 120) % 360, s, l],
      [(h + 240) % 360, s, l],
      this.adjustColor(baseHsl, { saturation: -15, lightness: -10 })
    ];
  }

  // Generate tetradic (square) colors
  static generateTetradic(baseHsl: [number, number, number]): [number, number, number][] {
    const [h, s, l] = baseHsl;
    
    return [
      baseHsl,
      [(h + 90) % 360, s, l],
      [(h + 180) % 360, s, l],
      [(h + 270) % 360, s, l],
      this.adjustColor(baseHsl, { saturation: -20, lightness: 25 })
    ];
  }

  // Generate split complementary colors
  static generateSplitComplementary(baseHsl: [number, number, number]): [number, number, number][] {
    const [h, s, l] = baseHsl;
    const complement = (h + 180) % 360;
    
    return [
      baseHsl,
      this.adjustColor(baseHsl, { hue: 30 }),
      [(complement - 30 + 360) % 360, s, l],
      [(complement + 30) % 360, s, l],
      this.adjustColor(baseHsl, { saturation: -25, lightness: 20 })
    ];
  }

  // Generate monochromatic colors
  static generateMonochromatic(baseHsl: [number, number, number]): [number, number, number][] {
    const [h, s, l] = baseHsl;
    
    return [
      [h, s, Math.max(5, l - 40)],   // Very dark
      [h, s, Math.max(15, l - 20)],  // Dark
      baseHsl,                        // Base
      [h, Math.max(10, s - 20), Math.min(95, l + 20)], // Light
      [h, Math.max(5, s - 30), Math.min(98, l + 35)]   // Very light
    ];
  }
}

// Convert RGB array to hex
const rgbToHex = (rgb: number[]): string => {
  return '#' + rgb.map(x => {
    const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

// Convert RGB to HSL for better color manipulation
const rgbToHsl = (r: number, g: number, b: number): string => {
  const [h, s, l] = ColorTheory.rgbToHsl(r, g, b);
  return `hsl(${h}, ${s}%, ${l}%)`;
};

// Improved API call with better error handling and timeout
const callColormindAPI = async (payload: any, timeout: number = 10000): Promise<any> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    console.log('Calling Colormind API with payload:', payload);
    
    const response = await fetch(COLORMIND_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
      mode: 'cors'
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Colormind API response:', data);
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    console.warn('Direct Colormind API failed:', error);
    throw error;
  }
};

// Try multiple API approaches for better reliability
const tryColormindAPI = async (payload: any): Promise<any> => {
  // First try direct API call
  try {
    return await callColormindAPI(payload, 8000);
  } catch (error) {
    console.warn('Direct API failed, trying proxy...');
  }

  // Try with CORS proxy
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 12000);

    const response = await fetch(COLORMIND_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      console.log('Proxy API response:', data);
      return data;
    }
  } catch (error) {
    console.warn('Proxy API failed:', error);
  }

  // If all API calls fail, generate a model-specific fallback
  console.log('All API calls failed, generating model-specific fallback for:', payload.model);
  return generateModelSpecificFallback(payload.model, payload.input);
};

// Generate authentic model-specific fallback palettes based on actual model characteristics
const generateModelSpecificFallback = (model: string, input?: any) => {
  const modelPalettes: Record<string, number[][]> = {
    // Anime/Artistic Models
    'akira': [
      [15, 15, 25],     // Very dark background
      [220, 20, 60],    // Crimson red
      [255, 20, 147],   // Deep pink/magenta
      [0, 255, 255],    // Cyan
      [255, 255, 0]     // Electric yellow
    ],
    'makoto_shinkai': [
      [135, 206, 235],  // Sky blue
      [255, 182, 193],  // Light pink
      [255, 218, 185],  // Peach
      [176, 196, 222],  // Light steel blue
      [240, 248, 255]   // Alice blue
    ],
    'cyberpunk': [
      [10, 10, 15],     // Very dark
      [255, 0, 150],    // Hot pink
      [0, 255, 255],    // Cyan
      [138, 43, 226],   // Blue violet
      [255, 20, 147]    // Deep pink
    ],
    
    // UI/Corporate Models
    'ui': [
      [248, 250, 252],  // Very light gray
      [226, 232, 240],  // Light gray
      [59, 130, 246],   // Blue
      [30, 64, 175],    // Dark blue
      [15, 23, 42]      // Very dark blue
    ],
    'corporate': [
      [240, 249, 255],  // Very light blue
      [147, 197, 253],  // Light blue
      [59, 130, 246],   // Blue
      [29, 78, 216],    // Dark blue
      [30, 58, 138]     // Very dark blue
    ],
    'minimal': [
      [255, 255, 255],  // White
      [249, 250, 251],  // Very light gray
      [156, 163, 175],  // Gray
      [75, 85, 99],     // Dark gray
      [31, 41, 55]      // Very dark gray
    ],

    // Nature Models
    'forest': [
      [240, 253, 244],  // Very light green
      [187, 247, 208],  // Light green
      [34, 197, 94],    // Green
      [21, 128, 61],    // Dark green
      [20, 83, 45]      // Very dark green
    ],
    'ocean': [
      [240, 249, 255],  // Very light blue
      [186, 230, 253],  // Light blue
      [14, 165, 233],   // Blue
      [7, 89, 133],     // Dark blue
      [12, 74, 110]     // Very dark blue
    ],
    'desert': [
      [255, 251, 235],  // Warm white
      [254, 215, 170],  // Light orange
      [251, 146, 60],   // Orange
      [194, 65, 12],    // Dark orange
      [124, 45, 18]     // Very dark orange
    ],
    'flower_photography': [
      [255, 242, 242],  // Very light pink
      [252, 165, 165],  // Light pink
      [244, 63, 94],    // Pink
      [190, 18, 60],    // Dark pink
      [136, 19, 55]     // Very dark pink
    ],

    // Seasonal Models
    'sunset': [
      [255, 237, 213],  // Light peach
      [255, 183, 77],   // Orange
      [255, 138, 101],  // Coral
      [207, 73, 57],    // Red
      [124, 45, 18]     // Dark brown
    ],
    'autumn': [
      [255, 247, 237],  // Cream
      [255, 183, 77],   // Orange
      [202, 138, 4],    // Yellow
      [153, 27, 27],    // Red
      [92, 25, 25]      // Dark red
    ],
    'spring': [
      [240, 253, 244],  // Light green
      [187, 247, 208],  // Green
      [252, 211, 77],   // Yellow
      [244, 63, 94],    // Pink
      [139, 69, 19]     // Brown
    ],
    'winter': [
      [248, 250, 252],  // Very light blue
      [191, 219, 254],  // Light blue
      [59, 130, 246],   // Blue
      [30, 64, 175],    // Dark blue
      [30, 58, 138]     // Very dark blue
    ],
    'summer': [
      [255, 251, 235],  // Light yellow
      [254, 240, 138],  // Yellow
      [34, 197, 94],    // Green
      [14, 165, 233],   // Blue
      [244, 63, 94]     // Pink
    ],

    // Style Models
    'vintage': [
      [245, 245, 220],  // Beige
      [210, 180, 140],  // Tan
      [160, 82, 45],    // Saddle brown
      [139, 69, 19],    // Saddle brown
      [101, 67, 33]     // Dark brown
    ],
    'modern_art': [
      [255, 255, 255],  // White
      [255, 193, 7],    // Amber
      [233, 30, 99],    // Pink
      [63, 81, 181],    // Indigo
      [33, 33, 33]      // Dark gray
    ],
    'neon': [
      [10, 10, 15],     // Very dark
      [255, 20, 147],   // Deep pink
      [0, 255, 127],    // Spring green
      [255, 69, 0],     // Red orange
      [138, 43, 226]    // Blue violet
    ],
    'pastel': [
      [255, 240, 245],  // Lavender blush
      [255, 182, 193],  // Light pink
      [221, 160, 221],  // Plum
      [173, 216, 230],  // Light blue
      [240, 230, 140]   // Khaki
    ],
    'monochrome': [
      [255, 255, 255],  // White
      [229, 231, 235],  // Very light gray
      [156, 163, 175],  // Gray
      [75, 85, 99],     // Dark gray
      [31, 41, 55]      // Very dark gray
    ],

    // Tone Models
    'earth_tones': [
      [245, 245, 220],  // Beige
      [222, 184, 135],  // Burlywood
      [160, 82, 45],    // Saddle brown
      [139, 69, 19],    // Saddle brown
      [101, 67, 33]     // Dark brown
    ],
    'jewel_tones': [
      [255, 215, 0],    // Gold
      [220, 20, 60],    // Crimson
      [75, 0, 130],     // Indigo
      [0, 100, 0],      // Dark green
      [128, 0, 128]     // Purple
    ],

    // Mood Models
    'bold': [
      [255, 255, 255],  // White
      [255, 0, 0],      // Red
      [0, 0, 255],      // Blue
      [255, 255, 0],    // Yellow
      [0, 0, 0]         // Black
    ],
    'soft': [
      [255, 250, 250],  // Snow
      [255, 228, 225],  // Misty rose
      [255, 182, 193],  // Light pink
      [221, 160, 221],  // Plum
      [230, 230, 250]   // Lavender
    ],
    'dramatic': [
      [25, 25, 25],     // Very dark gray
      [220, 20, 60],    // Crimson
      [255, 215, 0],    // Gold
      [128, 0, 128],    // Purple
      [0, 0, 0]         // Black
    ],
    'creative': [
      [255, 20, 147],   // Deep pink
      [255, 165, 0],    // Orange
      [50, 205, 50],    // Lime green
      [138, 43, 226],   // Blue violet
      [255, 69, 0]      // Red orange
    ]
  };

  // Use model-specific palette or generate one based on the model name
  let palette = modelPalettes[model] || modelPalettes['default'];
  
  if (!palette) {
    // Generate palette based on model characteristics if not found
    const isDark = ['cyberpunk', 'neon', 'dramatic', 'bold'].includes(model);
    const isWarm = ['sunset', 'autumn', 'earth_tones', 'desert'].includes(model);
    const isCool = ['ocean', 'winter', 'corporate', 'ui'].includes(model);
    
    palette = generateThemedPalette(isDark, isWarm, isCool);
  }

  // If input colors are provided (locked colors), try to incorporate them
  if (input && Array.isArray(input)) {
    const result = [...palette];
    input.forEach((color, index) => {
      if (color && Array.isArray(color) && color.length === 3) {
        result[index] = color;
      }
    });
    palette = result;
  }

  return { result: palette };
};

// Generate themed palette for fallback
const generateThemedPalette = (isDark: boolean, isWarm: boolean, isCool: boolean): number[][] => {
  const baseHue = Math.floor(Math.random() * 360);
  const hue = isWarm ? (baseHue + 30) % 360 : isCool ? (baseHue + 180) % 360 : baseHue;
  
  if (isDark) {
    return [
      [10, 10, 15],
      [30, 30, 45],
      [Math.floor(Math.sin(hue * Math.PI / 180) * 127 + 128), 
       Math.floor(Math.cos(hue * Math.PI / 180) * 127 + 128), 
       Math.floor(Math.sin((hue + 120) * Math.PI / 180) * 127 + 128)],
      [40, 40, 60],
      [15, 15, 25]
    ];
  } else {
    return [
      [248, 250, 252],
      [226, 232, 240],
      [Math.floor(Math.sin(hue * Math.PI / 180) * 100 + 100), 
       Math.floor(Math.cos(hue * Math.PI / 180) * 100 + 100), 
       Math.floor(Math.sin((hue + 120) * Math.PI / 180) * 100 + 100)],
      [64, 64, 80],
      [30, 30, 45]
    ];
  }
};

// Generate palette using Colormind API with improved fallback
export const generateColormindPalette = async (model: string = 'default'): Promise<ColorPalette> => {
  try {
    console.log(`Generating palette with model: ${model}`);
    
    const payload = { model };
    const data = await tryColormindAPI(payload);
    
    if (!data || !data.result || !Array.isArray(data.result)) {
      throw new Error('Invalid API response format');
    }
    
    const colors = data.result.map((rgb: number[]) => {
      if (!Array.isArray(rgb) || rgb.length !== 3) {
        throw new Error('Invalid color format in API response');
      }
      return rgbToHsl(rgb[0], rgb[1], rgb[2]);
    });
    
    console.log(`Successfully generated ${model} palette:`, colors);
    
    return {
      colors,
      names: ['Primary', 'Secondary', 'Accent', 'Dark', 'Darkest']
    };
  } catch (error) {
    console.warn(`Colormind API failed for model ${model}, using enhanced fallback:`, error);
    
    // Use model-specific fallback
    const fallbackData = generateModelSpecificFallback(model);
    const colors = fallbackData.result.map((rgb: number[]) => rgbToHsl(rgb[0], rgb[1], rgb[2]));
    
    return {
      colors,
      names: ['Primary', 'Secondary', 'Accent', 'Dark', 'Darkest']
    };
  }
};

// Generate palette with locked colors (partial palette generation)
export const generateWithLockedColors = async (
  lockedColors: (number[] | null)[], 
  model: string = 'default'
): Promise<ColorPalette> => {
  try {
    console.log(`Generating palette with locked colors for model: ${model}`, lockedColors);
    
    const payload = {
      model,
      input: lockedColors
    };
    
    const data = await tryColormindAPI(payload);
    
    if (!data || !data.result || !Array.isArray(data.result)) {
      throw new Error('Invalid API response format');
    }
    
    const colors = data.result.map((rgb: number[]) => {
      if (!Array.isArray(rgb) || rgb.length !== 3) {
        throw new Error('Invalid color format in API response');
      }
      return rgbToHsl(rgb[0], rgb[1], rgb[2]);
    });
    
    console.log(`Successfully generated ${model} palette with locked colors:`, colors);
    
    return {
      colors,
      names: ['Primary', 'Secondary', 'Accent', 'Dark', 'Darkest']
    };
  } catch (error) {
    console.warn(`Colormind API failed for model ${model} with locked colors, using enhanced fallback:`, error);
    
    // Use model-specific fallback with locked colors
    const fallbackData = generateModelSpecificFallback(model, lockedColors);
    const colors = fallbackData.result.map((rgb: number[]) => rgbToHsl(rgb[0], rgb[1], rgb[2]));
    
    return {
      colors,
      names: ['Primary', 'Secondary', 'Accent', 'Dark', 'Darkest']
    };
  }
};

// Enhanced local palette generation with advanced color theory
export const generateHarmoniousPalette = (model: string = 'default'): ColorPalette => {
  // Generate base color with model-specific constraints
  const baseColor = generateBaseColorForModel(model);
  const baseHsl: [number, number, number] = ColorTheory.rgbToHsl(baseColor[0], baseColor[1], baseColor[2]);
  
  let colors: [number, number, number][];
  
  // Apply color theory based on model
  switch (model) {
    case 'analogous':
      colors = ColorTheory.generateAnalogous(baseHsl);
      break;
    case 'complementary':
      colors = ColorTheory.generateComplementary(baseHsl);
      break;
    case 'triadic':
      colors = ColorTheory.generateTriadic(baseHsl);
      break;
    case 'tetradic':
    case 'square':
      colors = ColorTheory.generateTetradic(baseHsl);
      break;
    case 'split_complementary':
      colors = ColorTheory.generateSplitComplementary(baseHsl);
      break;
    case 'monochromatic':
      colors = ColorTheory.generateMonochromatic(baseHsl);
      break;
    default:
      colors = generateModelSpecificPalette(model, baseHsl);
  }
  
  // Convert to HSL strings
  const hslColors = colors.map(([h, s, l]) => `hsl(${h}, ${s}%, ${l}%)`);
  
  return {
    colors: hslColors,
    names: ['Background', 'Light', 'Primary', 'Dark', 'Darkest']
  };
};

// Generate base color based on model characteristics
const generateBaseColorForModel = (model: string): [number, number, number] => {
  const modelConstraints: Record<string, any> = {
    'cyberpunk': { hueRange: [280, 320], saturationRange: [70, 100], lightnessRange: [40, 70] },
    'neon': { hueRange: [0, 360], saturationRange: [80, 100], lightnessRange: [50, 80] },
    'forest': { hueRange: [90, 150], saturationRange: [40, 80], lightnessRange: [30, 70] },
    'ocean': { hueRange: [180, 240], saturationRange: [50, 90], lightnessRange: [40, 80] },
    'sunset': { hueRange: [0, 60], saturationRange: [60, 100], lightnessRange: [50, 80] },
    'desert': { hueRange: [20, 60], saturationRange: [40, 80], lightnessRange: [40, 70] },
    'pastel': { hueRange: [0, 360], saturationRange: [20, 60], lightnessRange: [70, 90] },
    'earth_tones': { hueRange: [20, 60], saturationRange: [30, 70], lightnessRange: [30, 60] },
    'jewel_tones': { hueRange: [0, 360], saturationRange: [70, 100], lightnessRange: [30, 60] },
    'corporate': { hueRange: [200, 240], saturationRange: [30, 70], lightnessRange: [40, 70] },
    'minimal': { hueRange: [0, 360], saturationRange: [10, 40], lightnessRange: [60, 90] }
  };

  const constraints = modelConstraints[model] || {
    hueRange: [0, 360],
    saturationRange: [30, 80],
    lightnessRange: [40, 70]
  };

  const [h, s, l] = ColorTheory.randomColor(constraints);
  return ColorTheory.hslToRgb(h, s, l);
};

// Generate model-specific palette using advanced algorithms
const generateModelSpecificPalette = (model: string, baseHsl: [number, number, number]): [number, number, number][] => {
  const [h, s, l] = baseHsl;
  
  // Model-specific palette generation
  const modelGenerators: Record<string, () => [number, number, number][]> = {
    'ui': () => [
      [h, Math.max(5, s - 40), Math.min(96, l + 40)],  // Very light background
      [h, Math.max(10, s - 20), Math.min(85, l + 20)], // Light surface
      baseHsl,                                           // Primary
      ColorTheory.adjustColor(baseHsl, { lightness: -20 }), // Dark
      ColorTheory.adjustColor(baseHsl, { lightness: -40 })  // Darkest
    ],
    
    'cyberpunk': () => [
      [h, s, Math.max(5, l - 50)],                      // Very dark
      ColorTheory.adjustColor(baseHsl, { hue: 60, saturation: 20 }), // Neon accent
      baseHsl,                                           // Primary neon
      ColorTheory.adjustColor(baseHsl, { hue: -60, saturation: 20 }), // Secondary neon
      [h, Math.max(10, s - 30), Math.min(90, l + 30)]  // Light contrast
    ],
    
    'vintage': () => [
      ColorTheory.adjustColor(baseHsl, { saturation: -30, lightness: 25 }), // Faded light
      ColorTheory.adjustColor(baseHsl, { saturation: -20, lightness: 10 }), // Muted
      ColorTheory.adjustColor(baseHsl, { saturation: -10 }),                // Base vintage
      ColorTheory.adjustColor(baseHsl, { saturation: -15, lightness: -15 }), // Dark vintage
      ColorTheory.adjustColor(baseHsl, { saturation: -25, lightness: -30 })  // Very dark
    ],
    
    'bold': () => [
      ColorTheory.adjustColor(baseHsl, { saturation: 30, lightness: 20 }), // Bright light
      ColorTheory.adjustColor(baseHsl, { saturation: 20 }),                // Enhanced
      baseHsl,                                                             // Bold base
      ColorTheory.adjustColor(baseHsl, { hue: 120, saturation: 20 }),     // Contrasting
      ColorTheory.adjustColor(baseHsl, { lightness: -30 })                // Deep
    ]
  };

  const generator = modelGenerators[model];
  if (generator) {
    return generator();
  }

  // Default sophisticated palette
  return [
    ColorTheory.adjustColor(baseHsl, { saturation: -20, lightness: 30 }), // Light
    ColorTheory.adjustColor(baseHsl, { saturation: -10, lightness: 15 }), // Medium light
    baseHsl,                                                               // Base
    ColorTheory.adjustColor(baseHsl, { saturation: 10, lightness: -15 }), // Dark
    ColorTheory.adjustColor(baseHsl, { saturation: 20, lightness: -30 })  // Darkest
  ];
};

// Convert HSL to hex (existing function)
export const hslToHex = (hsl: string): string => {
  const match = hsl.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  if (!match) return hsl;
  
  const h = parseInt(match[1]) / 360;
  const s = parseInt(match[2]) / 100;
  const l = parseInt(match[3]) / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Convert hex to RGB array for API input
export const hexToRgb = (hex: string): number[] | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : null;
};